import "styles/patient-dash.scss";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import Button from "@mui/material/Button";
import FamilyService from "services/FamilyService.js";
import PatientFormService from "services/PatientFormService.js";
import { selectAuthUser } from "state/slices/authUserSlice";

const PatientDashboard = () => {
  const navigate = useNavigate();
  const authUser = useSelector(selectAuthUser);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    familyCount: 0,
    totalForms: 0,
    completedForms: 0,
    remainingForms: 0,
    completionStatus: "",
    latestPatientName: "",
    latestPatientDob: "",
    continueRegistrationPath: "/patient/intakes",
    hasIncompleteForms: false,
  });

  useEffect(() => {
    let isMounted = true;

    async function loadDashboardSummary() {
      setLoading(true);
      try {
        const membersResponse = await FamilyService.getMembers();
        const members = membersResponse.data || [];

        const summaries = await Promise.all(
          members.map(async (member) => {
            try {
              const intakeResponse = await PatientFormService.getIntakePacks(member.id);
              const intakePacks = intakeResponse.data || [];
              if (!intakePacks.length) {
                return { member, latestPack: null, forms: [] };
              }

              const latestPack = [...intakePacks].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              )[0];

              const formsResponse = await PatientFormService.getIntakeForms([
                member.id,
                latestPack.id,
              ]);

              return {
                member,
                latestPack,
                forms: formsResponse.data || [],
              };
            } catch (error) {
              console.error("Unable to load patient form summary", error);
              return { member, latestPack: null, forms: [] };
            }
          })
        );

        const allForms = summaries.flatMap((summary) => summary.forms);
        const totalForms = allForms.length;
        const completedForms = allForms.filter(
          (form) => form.formStatusTypeEnum === "SUBMITTED"
        ).length;

        const remainingForms = Math.max(totalForms - completedForms, 0);
        const completionStatus =
          remainingForms === 0 && totalForms > 0
            ? "All registration forms completed"
            : remainingForms > 0
              ? `${remainingForms} task${remainingForms === 1 ? "" : "s"} pending`
              : "Registration has not started yet";

        let continueRegistrationPath = "/patient/intakes";
        let hasIncompleteForms = false;
        const nextIncompleteGroup = summaries.find((summary) =>
          summary.latestPack &&
          summary.forms.some((form) => form.formStatusTypeEnum !== "SUBMITTED")
        );

        if (nextIncompleteGroup) {
          continueRegistrationPath = `/patient/${nextIncompleteGroup.member.id}/intakes/${nextIncompleteGroup.latestPack.id}`;
          hasIncompleteForms = true;
        }

        const latestPatient = members[0] || {};

        if (isMounted) {
          setStats({
            familyCount: members.length,
            totalForms,
            completedForms,
            remainingForms,
            completionStatus,
            latestPatientName: `${latestPatient.firstName || ""} ${latestPatient.lastName || ""}`.trim(),
            latestPatientDob: latestPatient.dob || "",
            continueRegistrationPath,
            hasIncompleteForms,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDashboardSummary();
    return () => {
      isMounted = false;
    };
  }, []);

  const progressPct = useMemo(() => {
    if (!stats.totalForms) return 0;
    return Math.round((stats.completedForms / stats.totalForms) * 100);
  }, [stats.completedForms, stats.totalForms]);

  const progressLabel = useMemo(() => {
    if (!stats.totalForms) return "No registration forms are available yet.";
    if (stats.remainingForms === 0) return "Registration complete. You are all set.";
    return `${stats.completedForms} of ${stats.totalForms} forms completed (${progressPct}%)`;
  }, [progressPct, stats.completedForms, stats.remainingForms, stats.totalForms]);

  const displayName = `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim() || "Patient";
  const displayEmail = authUser.email || "Not available";
  const displayPatientName = stats.latestPatientName || "Not available";
  const displayDob = stats.latestPatientDob || "Not available";

  const dashboardCards = [
    {
      id: "intakes",
      title: "Registration Forms",
      description: "Start or continue required intake forms before your visit.",
      cta: "Open Intake Forms",
      path: "/patient/intakes",
      icon: <DescriptionRoundedIcon fontSize="large" color="primary" />,
      helper: `${stats.completedForms} completed, ${stats.remainingForms} remaining`,
      tooltip: "Use this section to complete required forms for each family member.",
    },
    {
      id: "family",
      title: "Patient Profiles",
      description: "Update family member details and review profile information.",
      cta: "Manage Profiles",
      path: "/patient/family",
      icon: <GroupRoundedIcon fontSize="large" color="success" />,
      helper: `${stats.familyCount} family ${stats.familyCount === 1 ? "member" : "members"}`,
      tooltip: "Keep names, demographics, and profile details up to date.",
    },
    {
      id: "programs",
      title: "Home Programs",
      description: "View completed programs and open reports for at-home activities.",
      cta: "View Programs & Reports",
      path: "/patient/stim-reports",
      icon: <AssignmentTurnedInRoundedIcon fontSize="large" color="secondary" />,
      helper: "Includes completed home programs",
      tooltip: "Home Programs contain completed plans and report details shared by your care team.",
    },
  ];

  return (
    <div className="patient-dash-container page-shell patient-dashboard-shell">
      <Box className="surface-card section-card patient-welcome-card">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Welcome, {displayName}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Use the cards below to manage forms, profiles, and home programs.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
              <Chip label={`Email: ${displayEmail}`} size="small" variant="outlined" className="patient-summary-chip" />
              <Chip label={`Patient: ${displayPatientName}`} size="small" variant="outlined" className="patient-summary-chip" />
              <Chip label={`DOB: ${displayDob}`} size="small" variant="outlined" className="patient-summary-chip" />
            </Stack>
          </Box>
          <Box className="patient-progress-card">
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="subtitle2">Registration Progress</Typography>
              <Tooltip title="Track completed forms and continue unfinished registration tasks here." placement="top">
                <InfoOutlinedIcon color="action" fontSize="small" className="patient-info-icon" />
              </Tooltip>
            </Stack>
            {loading ? (
              <Skeleton variant="rounded" height={10} sx={{ my: 1.25 }} />
            ) : (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{ mt: 1.25, mb: 1, height: 12, borderRadius: 10 }}
                  className="patient-progress-bar"
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {progressLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.completionStatus}
                </Typography>
                {stats.hasIncompleteForms ? (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(stats.continueRegistrationPath)}
                    sx={{ mt: 0.75, px: 0, textTransform: "none", fontWeight: 600 }}
                  >
                    Continue Registration
                  </Button>
                ) : stats.totalForms === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    Your care team has not assigned forms yet.
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <Grid
        container
        spacing={3}
        alignItems="stretch"
        className="patient-dashboard-grid"
      >
        {dashboardCards.map((item) => (
          <Grid item xs={12} sm={6} lg={4} key={item.id}>
            <Card className="patient-dashboard-card" elevation={0}>
              <CardActionArea
                onClick={() => navigate(item.path)}
                sx={{ height: "100%", p: 0 }}
              >
                <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    {item.icon}
                    <Tooltip title={item.tooltip} placement="top">
                      <InfoOutlinedIcon color="action" fontSize="small" className="patient-info-icon" />
                    </Tooltip>
                  </Stack>

                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.description}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
                    {loading ? "Loading summary..." : item.helper}
                  </Typography>

                  <Box sx={{ mt: "auto", pt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="button" color="primary.main">
                      {item.cta}
                    </Typography>
                    <ArrowForwardRoundedIcon color="primary" fontSize="small" />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default PatientDashboard;